import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import SubscriptionBanner from './SubscriptionBanner';
import SuspendedModal from './SuspendedModal';
import NoSubscriptionModal from './NoSubscriptionModal';

const SubscriptionGuard = ({ children }) => {
  const {
    loading,
    subscriptionStatus,
    isGrace,
    isFullyBlocked,
    hasNoSubscription,
    access,
    graceDismissed,
    setGraceDismissed,
    canManageBilling,
  } = useSubscription();
  const location = useLocation();

  // Always allow access to billing pages
  const isBillingPage = location.pathname.startsWith('/billing');
  if (isBillingPage) return <>{children}</>;

  // Don't render guard while loading (avoid flash)
  if (loading) return <>{children}</>;

  // Suspended — full-screen blocking modal (children not rendered). This is
  // the only subscription state teachers should ever see; everything else
  // below is billing/trial nagging that's only relevant to whoever can
  // actually act on it.
  if (isFullyBlocked) {
    return (
      <SuspendedModal
        message={access?.message}
        canManageBilling={canManageBilling}
      />
    );
  }

  if (!canManageBilling) return <>{children}</>;

  // No subscription exists at all
  if (hasNoSubscription) {
    return (
      <>
        <NoSubscriptionModal />
        {children}
      </>
    );
  }

  return (
    <>
      {/* Pending banner — invoice created, payment not yet made */}
      {subscriptionStatus === 'pending' && !graceDismissed && (
        <SubscriptionBanner
          type="pending"
          message={access?.message || 'A new subscription invoice is available. Complete your payment to stay up to date.'}
          onDismiss={() => setGraceDismissed(true)}
          canManageBilling={canManageBilling}
        />
      )}

      {/* Grace period banner */}
      {isGrace && !graceDismissed && (
        <SubscriptionBanner
          type="grace"
          message={access?.message || 'Your payment is due. Please complete your subscription payment.'}
          onDismiss={() => setGraceDismissed(true)}
          canManageBilling={canManageBilling}
        />
      )}

      {/* Restricted banner */}
      {subscriptionStatus === 'restricted' && (
        <SubscriptionBanner
          type="restricted"
          message={access?.message || 'Your account is restricted. Payment is 14+ days overdue. Write access has been disabled.'}
          onDismiss={null}
          canManageBilling={canManageBilling}
        />
      )}

      {children}
    </>
  );
};

export default SubscriptionGuard;
